import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Star, Image as ImageIcon, Search, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
// Resuelve URL absoluta para imágenes relativas (/api/uploads/file/...)
const resolveUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);

const ImageUploader = ({ images = [], mainIndex = 0, onChange, onMainChange }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const inputRef = useRef(null);

  const uploadOne = async (file) => {
    if (!/^image\/(jpeg|jpg|png)$/i.test(file.type)) {
      toast.error(`${file.name}: solo JPG o PNG`);
      return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`${file.name}: máximo 8MB`);
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/uploads/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Error subiendo ${file.name}`);
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter(Boolean);
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      const url = await uploadOne(f);
      if (url) uploaded.push(url);
    }
    setUploading(false);
    if (uploaded.length > 0) {
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onFileInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (images.includes(u)) {
      toast.error('Esa imagen ya está agregada');
      return;
    }
    onChange([...images, u]);
    setUrlInput('');
  };

  const removeAt = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
    // Ajustar main
    if (onMainChange) {
      if (idx === mainIndex) onMainChange(0);
      else if (idx < mainIndex) onMainChange(mainIndex - 1);
    }
  };

  const setMain = (idx) => {
    onMainChange && onMainChange(idx);
    toast.success('Imagen principal actualizada');
  };

  const addFromLibrary = (urls) => {
    const newOnes = urls.filter((u) => !images.includes(u));
    if (newOnes.length === 0) {
      toast.info('Ya estaban agregadas');
      return;
    }
    onChange([...images, ...newOnes]);
    toast.success(`${newOnes.length} agregada(s) de la biblioteca`);
    setLibraryOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="image-uploader">
      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="image-dropzone"
        className={`border-2 border-dashed cursor-pointer transition-colors px-4 py-6 sm:py-8 text-center ${
          dragOver ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/15 hover:border-white/30 bg-[#0A0A0A]'
        }`}
      >
        <Upload className={`w-6 h-6 mx-auto mb-2 ${dragOver ? 'text-emerald-400' : 'text-white/40'}`} />
        <p className="text-sm text-white/70">
          {uploading ? 'Subiendo imágenes...' : 'Arrastra imágenes aquí o haz click para seleccionar'}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-1">JPG o PNG · Máx 8MB · Múltiples</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          multiple
          hidden
          onChange={onFileInput}
          data-testid="image-file-input"
        />
      </div>

      {/* URL + Biblioteca */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
            placeholder="https://... (agregar por URL)"
            data-testid="image-url-input"
            className="flex-1 bg-[#121212] border border-white/10 focus:border-white/30 px-3 py-2.5 text-white text-sm outline-none"
          />
          <button
            type="button"
            onClick={addUrl}
            data-testid="image-url-add"
            className="px-4 py-2.5 bg-white/10 border border-white/20 hover:bg-white/20 text-xs uppercase tracking-wider"
          >
            Agregar
          </button>
        </div>
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          data-testid="open-media-library"
          className="inline-flex items-center gap-2 justify-center px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-300 text-xs uppercase tracking-wider"
        >
          <ImageIcon className="w-3.5 h-3.5" />
          Biblioteca
        </button>
      </div>

      {/* Thumbnails */}
      {images.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-2">
            {images.length} imagen(es) · La estrella marca la principal
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {images.map((img, idx) => {
              const isMain = idx === mainIndex;
              return (
                <div
                  key={img + idx}
                  data-testid={`image-thumb-${idx}`}
                  className={`relative aspect-square bg-[#0A0A0A] border-2 ${isMain ? 'border-amber-400' : 'border-white/10'}`}
                >
                  <img src={resolveUrl(img)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  {isMain && (
                    <div className="absolute top-1 left-1 bg-amber-400 text-black px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold flex items-center gap-0.5">
                      <Star className="w-2.5 h-2.5 fill-black" />
                      Principal
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex divide-x divide-white/10 bg-black/70">
                    {!isMain && (
                      <button
                        type="button"
                        onClick={() => setMain(idx)}
                        data-testid={`image-set-main-${idx}`}
                        className="flex-1 py-1.5 text-[10px] text-white/70 hover:text-amber-400 hover:bg-amber-500/10 flex items-center justify-center gap-0.5"
                        title="Marcar como principal"
                      >
                        <Star className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      data-testid={`image-remove-${idx}`}
                      className="flex-1 py-1.5 text-[10px] text-white/70 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"
                      title="Quitar"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {libraryOpen && (
        <MediaLibraryModal
          onClose={() => setLibraryOpen(false)}
          onPick={addFromLibrary}
          alreadySelected={images}
        />
      )}
    </div>
  );
};

// -----------------------------------------
// Media Library modal (reutilizable)
// -----------------------------------------
export const MediaLibraryModal = ({ onClose, onPick, alreadySelected = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState([]); // array of urls
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/uploads/media', { params: { limit: 500 } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Error cargando biblioteca');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (url) => {
    setSelection((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));
  };

  const remove = async (id) => {
    if (!window.confirm('¿Quitar esta imagen de la biblioteca? (los productos que la usan seguirán mostrándola)')) return;
    try {
      await api.delete(`/uploads/media/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Eliminada de la biblioteca');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const filtered = search
    ? items.filter((it) => (it.original_filename || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/10 w-full sm:max-w-5xl max-h-[92vh] flex flex-col"
        data-testid="media-library-modal"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-emerald-400" />
              Biblioteca de Medios
            </h3>
            <p className="text-[11px] text-white/40 mt-0.5">{total} imagen(es) subidas · Click para seleccionar</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-4 sm:px-6 py-3 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre..."
              data-testid="library-search"
              className="w-full bg-[#121212] border border-white/10 focus:border-white/30 pl-10 pr-3 py-2.5 text-sm outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {loading ? (
            <p className="text-center py-12 text-white/40 text-sm">Cargando biblioteca...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center py-12 text-white/40 text-sm">
              {items.length === 0 ? 'La biblioteca está vacía. Sube tus primeras imágenes desde el formulario del producto.' : 'Sin resultados para tu búsqueda.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {filtered.map((it) => {
                const isSel = selection.includes(it.url);
                const inUse = alreadySelected.includes(it.url);
                return (
                  <div
                    key={it.id}
                    data-testid={`library-item-${it.id}`}
                    className={`relative bg-[#121212] border-2 transition-all ${isSel ? 'border-emerald-500' : inUse ? 'border-white/30' : 'border-white/10 hover:border-white/40'}`}
                  >
                    <button type="button" onClick={() => toggle(it.url)} className="block w-full text-left">
                      <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                        <img src={resolveUrl(it.url)} alt={it.original_filename} className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                        {isSel && (
                          <div className="absolute top-2 right-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-black">
                            <Check className="w-4 h-4" strokeWidth={3} />
                          </div>
                        )}
                        {inUse && !isSel && (
                          <div className="absolute top-1.5 right-1.5 bg-white/20 backdrop-blur-sm text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">
                            En uso
                          </div>
                        )}
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="text-[10px] text-white/60 truncate" title={it.original_filename}>{it.original_filename}</p>
                        <p className="text-[9px] text-white/30 mt-0.5">
                          {(it.size / 1024).toFixed(0)} KB · {new Date(it.created_at).toLocaleDateString('es-CO')}
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                      data-testid={`library-delete-${it.id}`}
                      className="absolute top-1.5 left-1.5 p-1.5 bg-black/60 hover:bg-red-500/80 rounded transition-colors"
                      title="Quitar de biblioteca"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-white/10 flex items-center justify-between gap-3 bg-[#070707]">
          <span className="text-xs text-white/50">
            {selection.length > 0 ? <><span className="text-emerald-400 font-semibold">{selection.length}</span> seleccionadas</> : 'Ninguna seleccionada'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-wider hover:bg-white/5">
              Cerrar
            </button>
            <button
              type="button"
              onClick={() => onPick(selection)}
              disabled={selection.length === 0}
              data-testid="library-add-selected"
              className="px-5 py-2 bg-white text-black text-xs uppercase tracking-wider font-semibold disabled:opacity-40"
            >
              Agregar {selection.length > 0 ? `(${selection.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
